import { Injectable, inject, signal, PLATFORM_ID, NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

export interface AdminNotification {
  type: 'USER_REGISTERED' | 'DOCUMENT_UPLOADED' | 'PROPERTY_CREATED';
  message: string;
  entityId: string;
  timestamp: string;
  read?: boolean;
}

const TYPE_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  USER_REGISTERED:   { label: 'Nouvelle inscription',   icon: 'pi-user-plus',    color: '#3b82f6' },
  DOCUMENT_UPLOADED: { label: 'Document uploadé',        icon: 'pi-file-arrow-up', color: '#f59e0b' },
  PROPERTY_CREATED:  { label: 'Nouveau bien',            icon: 'pi-building',      color: '#10b981' },
};

// Reconnexion : 1 s, 2 s, 4 s… plafonnée à 30 s, remise à zéro dès que le flux est établi.
const RETRY_BASE_MS = 1_000;
const RETRY_MAX_MS  = 30_000;

/**
 * Flux SSE des notifications admin (property-api). EventSource ne pouvant pas envoyer
 * d'en-tête Authorization, on échange d'abord le JWT contre un ticket SSE de 60 s
 * (POST /admin/sse/ticket, qui passe par l'intercepteur : JWT ajouté et rafraîchi si
 * expiré), puis on ouvre le flux avec ce ticket. À chaque coupure on redemande un ticket
 * — la reconnexion automatique d'EventSource réutiliserait l'ancien, déjà expiré.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private platformId = inject(PLATFORM_ID);
  private auth       = inject(AuthService);
  private http       = inject(HttpClient);
  private zone       = inject(NgZone);

  private eventSource: EventSource | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelay = RETRY_BASE_MS;
  private active = false;

  notifications = signal<AdminNotification[]>([]);
  unreadCount   = signal(0);
  connected     = signal(false);

  metaFor(type: string) {
    return TYPE_LABELS[type] ?? { label: type, icon: 'pi-bell', color: '#6b7280' };
  }

  connect(): void {
    if (!isPlatformBrowser(this.platformId) || this.active) return;
    if (!this.auth.getToken()) return;
    this.active = true;
    this.openStream();
  }

  disconnect(): void {
    this.active = false;
    this.clearRetry();
    this.closeStream();
    this.retryDelay = RETRY_BASE_MS;
  }

  markAllRead(): void {
    this.notifications.update(n => n.map(notif => ({ ...notif, read: true })));
    this.unreadCount.set(0);
  }

  private openStream(): void {
    this.http.post<{ ticket: string }>(`${environment.apis.property}/admin/sse/ticket`, {}).subscribe({
      next: ({ ticket }) => {
        if (!this.active) return; // disconnect() pendant la demande de ticket
        const url = `${environment.apis.property}/admin/sse/stream?ticket=${encodeURIComponent(ticket)}`;
        const es = new EventSource(url);
        this.eventSource = es;

        es.addEventListener('connected', () => this.zone.run(() => {
          this.connected.set(true);
          this.retryDelay = RETRY_BASE_MS;
        }));

        es.addEventListener('notification', (e: MessageEvent) => this.zone.run(() => {
          try {
            const notification: AdminNotification = JSON.parse(e.data);
            this.notifications.update(n => [notification, ...n].slice(0, 100));
            this.unreadCount.update(c => c + 1);
          } catch { /* message illisible : ignoré */ }
        }));

        es.onerror = () => this.zone.run(() => this.scheduleReconnect());
      },
      // Ticket refusé (JWT invalide et non rafraîchissable → l'intercepteur déconnecte déjà) ou API injoignable.
      error: () => this.scheduleReconnect()
    });
  }

  private scheduleReconnect(): void {
    this.closeStream();
    if (!this.active || this.retryTimer || !this.auth.getToken()) return;
    const delay = this.retryDelay;
    this.retryDelay = Math.min(this.retryDelay * 2, RETRY_MAX_MS);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.active) this.openStream();
    }, delay);
  }

  private closeStream(): void {
    this.eventSource?.close();
    this.eventSource = null;
    this.connected.set(false);
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }
}
